'use client'

import { useMemo, useCallback, useEffect } from 'react'
import {
    ReactFlow,
    MiniMap,
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    addEdge,
    Connection,
    Edge,
    Node,
    Handle,
    Position,
} from 'reactflow'
import { Badge } from '@/components/ui/badge'
import { TpaLiveStatus } from '@/components/tagadapay/tpa-live-status'
import { getIntegrationImage } from '@/lib/utils/integration-utils'
import 'reactflow/dist/style.css'

interface PaymentFlowDiagramProps {
    paymentFlow: any
    compact?: boolean
}

const ProcessorNode = ({
    data,
}: {
    data: {
        processor?: {
            name: string
            type: string
            monthlyProcessingCapacity?: number
            baseCurrency?: string
            tagadapayAccountId?: string | null
            tagadapayStatus?: string | null
            tagadapayMode?: string | null
        }
        config?: { disabled?: boolean; weight?: number }
        isStart?: boolean
        isFallback?: boolean
        order?: number
        isSuccess?: boolean
        isFailure?: boolean
        isDecision?: boolean
        isRouting?: boolean
    }
}) => {
    const {
        processor,
        config,
        isStart,
        isFallback,
        order,
        isSuccess,
        isFailure,
        isDecision,
        isRouting,
    } = data

    const formatCapacity = (capacity?: number) => {
        if (!capacity || capacity === 0) return 'No limit'
        if (capacity >= 1000000) return `${(capacity / 1000000).toFixed(1)}M`
        if (capacity >= 1000) return `${(capacity / 1000).toFixed(1)}K`
        return capacity.toString()
    }

    return (
        <div
            className={`rounded-lg border-2 bg-white p-3 shadow-lg transition-all ${isStart
                    ? 'border-blue-500 bg-blue-50'
                    : isSuccess
                        ? 'border-green-500 bg-green-50'
                        : isFailure
                            ? 'border-red-500 bg-red-50'
                            : isDecision || isRouting
                                ? 'border-purple-500 bg-purple-50'
                                : isFallback
                                    ? 'border-orange-500 bg-orange-50'
                                    : config?.disabled
                                        ? 'border-gray-300 bg-gray-50'
                                        : 'border-green-500 bg-green-50'
                }`}
            style={{ minWidth: 200 }}
        >
            {!isStart && (
                <>
                    <Handle
                        type="target"
                        position={Position.Left}
                        className="!bg-gray-400"
                    />
                    <Handle
                        type="source"
                        position={Position.Right}
                        className="!bg-gray-400"
                    />
                </>
            )}

            {isStart ? (
                <div className="text-center">
                    <div className="rounded-full bg-blue-500 p-2 text-white">
                        <span className="text-sm font-semibold">START</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-600">Transaction Initiated</p>
                </div>
            ) : isSuccess ? (
                <div className="text-center">
                    <div className="rounded-full bg-green-500 p-2 text-white">
                        <span className="text-sm font-semibold">SUCCESS</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-600">Transaction Completed</p>
                </div>
            ) : isFailure ? (
                <div className="text-center">
                    <div className="rounded-full bg-red-500 p-2 text-white">
                        <span className="text-sm font-semibold">FAILURE</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-600">Transaction Failed</p>
                </div>
            ) : isDecision ? (
                <div className="text-center">
                    <div className="rounded-full bg-purple-500 p-2 text-white">
                        <span className="text-sm font-semibold">RESULT</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-600">Success or Failure?</p>
                </div>
            ) : isRouting ? (
                <div className="text-center">
                    <div className="rounded-full bg-purple-500 p-2 text-white">
                        <span className="text-sm font-semibold">ROUTE</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-600">{processor?.type}</p>
                </div>
            ) : (
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        {processor?.type && (
                            <img
                                src={getIntegrationImage(processor.type)}
                                alt={`${processor.type} icon`}
                                className="size-5 rounded object-contain"
                            />
                        )}
                        <div className="flex-1">
                            <h4 className="text-sm font-semibold text-gray-800">
                                {processor?.name ?? 'Unknown Processor'}
                            </h4>
                            <p className="text-xs text-gray-500">{processor?.type}</p>
                            <TpaLiveStatus
                                tpaId={processor?.tagadapayAccountId}
                                status={processor?.tagadapayStatus}
                                mode={processor?.tagadapayMode}
                                compact
                                className="mt-0.5"
                            />
                        </div>
                    </div>

                    {processor?.monthlyProcessingCapacity !== undefined && (
                        <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-500">Capacity:</span>
                            <Badge variant="secondary" className="text-xs">
                                {formatCapacity(processor.monthlyProcessingCapacity)}
                                {processor?.baseCurrency ? ` ${processor.baseCurrency}` : ''}
                            </Badge>
                        </div>
                    )}

                    <div className="flex flex-wrap gap-1">
                        {isFallback && (
                            <Badge variant="outline" className="text-xs">
                                Fallback #{order}
                            </Badge>
                        )}
                        {config?.weight && (
                            <Badge variant="secondary" className="text-xs">
                                {config.weight}%
                            </Badge>
                        )}
                        <Badge
                            variant={config?.disabled ? 'destructive' : 'default'}
                            className="text-xs"
                        >
                            {config?.disabled ? 'Disabled' : 'Active'}
                        </Badge>
                    </div>
                </div>
            )}
        </div>
    )
}

const nodeTypes = {
    processor: ProcessorNode,
}

export const PaymentFlowDiagram = ({ paymentFlow, compact = false }: PaymentFlowDiagramProps) => {
    const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
        const nodes: Node[] = []
        const edges: Edge[] = []

        nodes.push({
            id: 'start',
            type: 'processor',
            position: { x: 50, y: 250 },
            data: { isStart: true },
            draggable: false,
        })

        let currentX = 300
        const primaryY = 150
        const fallbackY = 400
        const nodeSpacing = 250
        const verticalSpacing = 120

        const enabledPrimaryConfigs =
            paymentFlow.processorConfigs?.filter((config: any) => !config.disabled) ??
            []

        if (enabledPrimaryConfigs.length > 0) {
            const routingNodeId = 'routing-decision'
            nodes.push({
                id: routingNodeId,
                type: 'processor',
                position: { x: currentX, y: 250 },
                data: {
                    processor: {
                        name:
                            paymentFlow.pickProcessorStrategy === 'weighted'
                                ? 'Custom Weights'
                                : 'Automatic',
                        type:
                            paymentFlow.pickProcessorStrategy === 'weighted'
                                ? 'Weight-based selection'
                                : 'Capacity-based selection',
                    },
                    config: {},
                    isRouting: true,
                },
                draggable: false,
            })

            edges.push({
                id: `start-${routingNodeId}`,
                source: 'start',
                target: routingNodeId,
                animated: true,
                style: { stroke: '#3b82f6', strokeWidth: 2 },
            })

            currentX += nodeSpacing

            enabledPrimaryConfigs.forEach((config: any, index: number) => {
                const nodeId = `primary-${config.processorId}`

                const totalHeight = (enabledPrimaryConfigs.length - 1) * verticalSpacing
                const startY = primaryY - totalHeight / 2

                nodes.push({
                    id: nodeId,
                    type: 'processor',
                    position: {
                        x: currentX,
                        y: startY + index * verticalSpacing,
                    },
                    data: {
                        processor: config.processor,
                        config,
                        isPrimary: true,
                    },
                    draggable: false,
                })

                edges.push({
                    id: `${routingNodeId}-${nodeId}`,
                    source: routingNodeId,
                    target: nodeId,
                    animated: false,
                    style: { stroke: '#10b981', strokeWidth: 2 },
                    label: config.weight ? `${config.weight}%` : undefined,
                })
            })

            currentX += nodeSpacing
        }

        const decisionNodeId = 'decision'
        nodes.push({
            id: decisionNodeId,
            type: 'processor',
            position: { x: currentX, y: 250 },
            data: {
                processor: { name: 'Transaction Result', type: 'Success or Failure?' },
                config: {},
                isDecision: true,
            },
            draggable: false,
        })

        enabledPrimaryConfigs.forEach((config: any) => {
            const nodeId = `primary-${config.processorId}`
            edges.push({
                id: `${nodeId}-${decisionNodeId}`,
                source: nodeId,
                target: decisionNodeId,
                animated: false,
                style: { stroke: '#6b7280', strokeWidth: 1 },
            })
        })

        currentX += nodeSpacing

        const successNodeId = 'success'
        nodes.push({
            id: successNodeId,
            type: 'processor',
            position: { x: currentX, y: 150 },
            data: {
                processor: { name: 'SUCCESS', type: 'Transaction Completed' },
                config: {},
                isSuccess: true,
            },
            draggable: false,
        })

        edges.push({
            id: `${decisionNodeId}-${successNodeId}`,
            source: decisionNodeId,
            target: successNodeId,
            animated: false,
            style: { stroke: '#10b981', strokeWidth: 2 },
            label: 'Success',
        })

        if (paymentFlow.fallbackMode && paymentFlow.fallbackProcessorConfigs?.length) {
            const fallbackConfigs = paymentFlow.fallbackProcessorConfigs.slice(
                0,
                paymentFlow.maxFallbackRetries ?? 0,
            )
            let lastFallbackId = decisionNodeId
            const fallbackX = currentX - nodeSpacing

            fallbackConfigs.forEach((config: any, index: number) => {
                const nodeId = `fallback-${config.processorId}`

                nodes.push({
                    id: nodeId,
                    type: 'processor',
                    position: {
                        x: fallbackX + index * nodeSpacing,
                        y: fallbackY,
                    },
                    data: {
                        processor: config.processor,
                        config,
                        isFallback: true,
                        order: index + 1,
                    },
                    draggable: false,
                })

                edges.push({
                    id: `${lastFallbackId}-${nodeId}`,
                    source: lastFallbackId,
                    target: nodeId,
                    animated: false,
                    style: { stroke: '#f59e0b', strokeWidth: 2 },
                    label: index === 0 ? 'Failure' : undefined,
                })

                edges.push({
                    id: `${nodeId}-${successNodeId}`,
                    source: nodeId,
                    target: successNodeId,
                    animated: false,
                    style: { stroke: '#10b981', strokeWidth: 1 },
                })

                lastFallbackId = nodeId
            })

            const failureNodeId = 'failure'
            nodes.push({
                id: failureNodeId,
                type: 'processor',
                position: {
                    x: fallbackX + fallbackConfigs.length * nodeSpacing,
                    y: fallbackY + 150,
                },
                data: {
                    processor: { name: 'FAILURE', type: 'Transaction Failed' },
                    config: {},
                    isFailure: true,
                },
                draggable: false,
            })

            edges.push({
                id: `${lastFallbackId}-${failureNodeId}`,
                source: lastFallbackId,
                target: failureNodeId,
                animated: false,
                style: { stroke: '#ef4444', strokeWidth: 2 },
                label: 'Final Failure',
            })
        } else {
            const failureNodeId = 'failure'
            nodes.push({
                id: failureNodeId,
                type: 'processor',
                position: { x: currentX, y: 350 },
                data: {
                    processor: { name: 'FAILURE', type: 'Transaction Failed' },
                    config: {},
                    isFailure: true,
                },
                draggable: false,
            })

            edges.push({
                id: `${decisionNodeId}-${failureNodeId}`,
                source: decisionNodeId,
                target: failureNodeId,
                animated: false,
                style: { stroke: '#ef4444', strokeWidth: 2 },
                label: 'Failure',
            })
        }

        return { nodes, edges }
    }, [
        paymentFlow.id,
        paymentFlow.name,
        paymentFlow.pickProcessorStrategy,
        paymentFlow.fallbackMode,
        paymentFlow.maxFallbackRetries,
        JSON.stringify(
            paymentFlow.processorConfigs?.map((c: any) => ({
                processorId: c.processorId,
                weight: c.weight,
                disabled: c.disabled,
                tagadapayAccountId: c.processor?.tagadapayAccountId,
                tagadapayStatus: c.processor?.tagadapayStatus,
            })),
        ),
        JSON.stringify(
            paymentFlow.fallbackProcessorConfigs?.map((c: any) => ({
                processorId: c.processorId,
                orderIndex: c.orderIndex,
            })),
        ),
    ])

    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)

    // Update nodes and edges when paymentFlow changes (for real-time preview)
    useEffect(() => {
        setNodes(initialNodes)
        setEdges(initialEdges)
    }, [initialNodes, initialEdges, setNodes, setEdges])

    const onConnect = useCallback(
        (params: Connection) => setEdges((eds) => addEdge(params, eds)),
        [setEdges],
    )

    return (
        <div className={compact ? "h-full w-full bg-gray-50" : "h-[600px] w-full rounded-lg border bg-gray-50"}>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                nodeTypes={nodeTypes}
                fitView
                attributionPosition="bottom-left"
                className="rounded-lg"
                panOnDrag={!compact}
                zoomOnScroll={!compact}
                zoomOnPinch={!compact}
                zoomOnDoubleClick={!compact}
            >
                <Background color="#aaa" gap={16} />
                {!compact && <Controls className="!bottom-4 !left-4" />}
                {!compact && (
                    <MiniMap
                        className="!bottom-4 !right-4"
                        nodeColor={(node) => {
                            if (node.data.isStart) return '#3b82f6'
                            if (node.data.isSuccess) return '#10b981'
                            if (node.data.isFailure) return '#ef4444'
                            if (node.data.isFallback) return '#f59e0b'
                            if (node.data.isDecision || node.data.isRouting) return '#8b5cf6'
                            return '#6b7280'
                        }}
                    />
                )}
            </ReactFlow>
        </div>
    )
}

